import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { Subscription, Observable, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';

import { LogoutService } from '../../features/admin/auth/services/logout.service';

import { ThemeService } from '../../core/services/theme.service';
import { AuthService } from '../../features/admin/auth/services/auth.service';
import { DialogService } from '../../core/services/dialog.service';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ProfileComponent } from '../../features/admin/profile/pages/profile.component';

@Component({
  imports: [CommonModule, RouterModule, SharedModule],
  selector: 'app-navbar',
  standalone: true,
  styleUrls: ['./navbar.component.scss'],
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit {
  permisos: any[] = [];
  menu$: Observable<any[]> = of([]);
  menuAbierto = false;
  quickLinks$: Observable<any[]> = of([]);

  constructor(
    private readonly authService: AuthService,
    private readonly logoutService: LogoutService,
    private readonly dialogService: DialogService,
    private readonly router: Router,
    public themeService: ThemeService,
    private dialog: MatDialog
  ) { }

  openProfile() {
    this.dialog.open(ProfileComponent, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'custom-modal-glass', // Assumes a global or component style, or default
      autoFocus: false
    });
  }

  ngOnInit() {
    const token = localStorage.getItem('token');
    if (!token) {
      this.router.navigate(['/unauthorized']);
      return;
    }

    const decodedToken: any = jwtDecode(token);
    const rolId = decodedToken?.rolId;
    if (!rolId) {
      this.router.navigate(['/dashboard']);
      return;
    }

    // 🔥 Cargar permisos y generar mapa de rutas sin alterar el menú
    this.menu$ = this.authService.getPermissions(rolId).pipe(
      tap((permisos: any) => {
        // Actualizar mapa de rutas como efecto secundario
        this.authService.setRutaMapFromCategorias(permisos);
      }),
      catchError(() => {
        this.router.navigate(['/unauthorized']);
        return of([]);
      })
    );

    // Extraer automáticamente las primeras rutas disponibles para el Bottom Nav Bar
    this.quickLinks$ = this.menu$.pipe(
      map(categorias => {
        const allRoutes: any[] = [];
        if (categorias && Array.isArray(categorias)) {
          categorias.forEach((c: any) => {
            if (c.rutas && Array.isArray(c.rutas)) {
              allRoutes.push(...c.rutas);
            }
          });
        }

        const quick: any[] = [];
        const findByKeyword = (kw: string) => allRoutes.find(r => r.nombre.toLowerCase().includes(kw) || r.ruta.toLowerCase().includes(kw));

        const finanzas = findByKeyword('finanza');
        const clientes = findByKeyword('cliente');
        const productos = findByKeyword('producto') || findByKeyword('venta') || findByKeyword('compra');

        if (finanzas) quick.push(finanzas);
        if (clientes) quick.push(clientes);
        if (productos) quick.push(productos);

        // Rellenar hasta tener 3 elementos por si no encontró los específicos
        for (const r of allRoutes) {
          if (quick.length >= 3) break;
          if (!quick.includes(r)) quick.push(r);
        }

        return quick;
      })
    );
  }

  logout() {
    this.dialogService.openConfirmDialog({
      title: 'Confirmación',
      message: '¿Está seguro de Cerrar Sesión?',
      confirmText: 'Sí',
      cancelText: 'No',
    }).afterClosed().subscribe((confirmado: boolean) => {
      if (confirmado) {
        this.logoutService.logout();
      }
    });
  }

  // 🔧 Construcción del menú agrupado por categorías
  buildMenuFromPermisos(permisos: any[]) {
    const categoriasMap = new Map();

    permisos.forEach(p => {
      const catId = p.idCategoria || p.categoriaId || p.categoria?.id || 0;
      const catNombre = p.categoriaNombre || p.categoria?.nombre || 'Sin categoría';
      const catIcono = p.categoriaIcono || p.categoria?.icono || 'fa-folder';

      if (!categoriasMap.has(catId)) {
        categoriasMap.set(catId, {
          id: catId,
          nombre: catNombre,
          icono: catIcono,
          rutas: []
        });
      }

      categoriasMap.get(catId).rutas.push({
        id: p.idRuta || p.rutaId || 0,
        nombre: p.rutaNombre || p.nombre || '',
        ruta: p.ruta || '',
        icon: p.icon || 'fa-circle'
      });
    });

    return Array.from(categoriasMap.values());
  }

  trackByCategory(index: number, categoria: any): any {
    return categoria.id || categoria.nombre || index;
  }

  trackByRoute(index: number, ruta: any): any {
    return ruta.id || ruta.ruta || index;
  }
}
